/**
 * The room's scenery: the light through the windows, the season, and the occasion.
 *
 * Nothing here is data about anybody. It is decoration worked out in the viewer's own browser from
 * the viewer's own clock: a manager in São Paulo at 3 pm sees an afternoon room, a colleague in
 * Lisbon at 7 pm the same room at dusk. The season comes from the month and the hemisphere, the
 * hemisphere from the browser's time zone — no location is asked for and none is sent anywhere.
 *
 * A viewer can pick a fixed look instead, or an image: the organization's, uploaded by a manager,
 * or their own, which stays in their browser.
 */

import { createContext, useContext } from 'react'

export type TimeOfDay = 'dawn' | 'day' | 'afternoon' | 'sunset' | 'night'
export type Season = 'spring' | 'summer' | 'autumn' | 'winter'

/** What a viewer chose. `auto` follows their clock and calendar. */
export type SceneryChoice = 'auto' | TimeOfDay | 'org-image' | 'my-image'

export const TIMES: TimeOfDay[] = ['dawn', 'day', 'afternoon', 'sunset', 'night']

export type Holiday =
  | 'newYear'
  | 'lunarNewYear'
  | 'valentines'
  | 'carnival'
  | 'womensDay'
  | 'easter'
  | 'earthDay'
  | 'programmersDay'
  | 'halloween'
  | 'diwali'
  | 'christmas'

/** What people wear. Worked out from the season and the occasion, never from the person. */
export type Outfit = 'hoodie' | 'tshirt' | 'scarf'
export type Headwear = 'santa' | 'bunny' | 'party' | 'witch' | 'mask' | null

export interface Scenery {
  time: TimeOfDay
  season: Season
  holiday: Holiday | null
  outfit: Outfit
  headwear: Headwear
  /** An image to draw on the back wall instead of the windows, as a URL. */
  image: string | null
}

export const DEFAULT_SCENERY: Scenery = {
  time: 'night',
  season: 'autumn',
  holiday: null,
  outfit: 'hoodie',
  headwear: null,
  image: null,
}

// ------------------------------------------------------------------ clock and calendar

/** The light at an hour of the viewer's day. */
export function timeOfDay(hour: number): TimeOfDay {
  if (hour >= 5 && hour < 7) return 'dawn'
  if (hour >= 7 && hour < 12) return 'day'
  if (hour >= 12 && hour < 17) return 'afternoon'
  if (hour >= 17 && hour < 19) return 'sunset'
  return 'night'
}

/**
 * Time zones south of the equator. Anything not listed is taken as northern, which is right for
 * most of the world's population and wrong only in the places named here.
 */
const SOUTHERN = [
  /^America\/(Sao_Paulo|Bahia|Fortaleza|Recife|Maceio|Belem|Araguaina|Cuiaba|Campo_Grande|Porto_Velho|Rio_Branco|Manaus|Santarem|Noronha|Eirunepe)$/,
  /^America\/(Argentina\/.*|Buenos_Aires|Cordoba|Mendoza|Santiago|Punta_Arenas|Montevideo|Asuncion|La_Paz|Lima)$/,
  /^Australia\//,
  /^Pacific\/(Auckland|Chatham|Fiji|Tongatapu|Apia|Noumea|Efate|Port_Moresby|Tahiti)$/,
  /^Africa\/(Johannesburg|Maputo|Harare|Lusaka|Windhoek|Gaborone|Maseru|Mbabane|Luanda|Blantyre|Lubumbashi|Dar_es_Salaam)$/,
  /^Indian\/(Antananarivo|Mauritius|Reunion|Mayotte|Comoro)$/,
  /^Atlantic\/(Stanley|South_Georgia)$/,
  /^Antarctica\//,
]

export function isSouthern(timeZone: string | undefined): boolean {
  return Boolean(timeZone && SOUTHERN.some((pattern) => pattern.test(timeZone)))
}

/** Meteorological seasons: whole months, which is what "it is summer" means to most people. */
export function seasonOf(month: number, southern: boolean): Season {
  // month is 0–11
  const north: Season = month <= 1 || month === 11 ? 'winter' : month <= 4 ? 'spring' : month <= 7 ? 'summer' : 'autumn'
  if (!southern) return north
  return ({ winter: 'summer', spring: 'autumn', summer: 'winter', autumn: 'spring' } as const)[north]
}

/** Easter Sunday in the Gregorian calendar (the anonymous "Meeus/Jones/Butcher" algorithm). */
export function easterSunday(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month - 1, day)
}

/** Lunisolar dates that no simple rule gives, for the years this build will be running. */
const LUNAR_NEW_YEAR: Record<number, [number, number]> = {
  2026: [1, 17], 2027: [1, 6], 2028: [0, 26], 2029: [1, 13], 2030: [1, 3], 2031: [0, 23], 2032: [1, 11],
}
const DIWALI: Record<number, [number, number]> = {
  2026: [10, 8], 2027: [9, 29], 2028: [9, 17], 2029: [10, 5], 2030: [9, 26], 2031: [10, 14], 2032: [10, 2],
}

const DAY = 86_400_000

function dayNumber(date: Date): number {
  return Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY)
}

function within(date: Date, start: Date, daysBefore: number, daysAfter: number): boolean {
  const n = dayNumber(date)
  const s = dayNumber(start)
  return n >= s - daysBefore && n <= s + daysAfter
}

/**
 * The occasion on a date, if any: days that are marked around most of the world, a few days
 * either side so the room is dressed for the week rather than for one day.
 */
export function holidayOn(date: Date): Holiday | null {
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()
  const on = (m: number, d: number, before = 0, after = 0) => within(date, new Date(year, m, d), before, after)

  if ((month === 11 && day >= 30) || (month === 0 && day <= 2)) return 'newYear'
  if (month === 11 && day >= 15 && day <= 26) return 'christmas'

  const easter = easterSunday(year)
  if (within(date, easter, 3, 1)) return 'easter'
  // Carnival: the Tuesday 47 days before Easter, with the weekend before it.
  const carnival = new Date(easter.getTime() - 47 * DAY)
  if (within(date, carnival, 4, 1)) return 'carnival'

  const lunar = LUNAR_NEW_YEAR[year]
  if (lunar && on(lunar[0], lunar[1], 1, 3)) return 'lunarNewYear'
  const diwali = DIWALI[year]
  if (diwali && on(diwali[0], diwali[1], 1, 1)) return 'diwali'

  if (on(1, 14, 1, 0)) return 'valentines'
  if (on(2, 8)) return 'womensDay'
  if (on(3, 22)) return 'earthDay'
  // The 256th day of the year: 13 September, or the 12th in a leap year.
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
  if (on(8, leap ? 12 : 13)) return 'programmersDay'
  if (on(9, 31, 6, 0)) return 'halloween'

  return null
}

/** What people wear for an occasion and a season. */
export function dressFor(season: Season, holiday: Holiday | null): { outfit: Outfit; headwear: Headwear } {
  const outfit: Outfit = season === 'summer' ? 'tshirt' : season === 'winter' ? 'scarf' : 'hoodie'
  const headwear: Headwear =
    holiday === 'christmas' ? 'santa'
      : holiday === 'easter' ? 'bunny'
        : holiday === 'newYear' ? 'party'
          : holiday === 'halloween' ? 'witch'
            : holiday === 'carnival' ? 'mask'
              : null
  return { outfit, headwear }
}

/**
 * The scenery for a viewer at a moment.
 *
 * `decorations` false leaves the occasion out — no hats, no garlands — for an organization that
 * would rather not; the light and the season still follow the clock.
 */
export function sceneryFor(
  choice: SceneryChoice,
  now: Date,
  timeZone: string | undefined,
  images: { org: string | null; mine: string | null },
  decorations = true,
): Scenery {
  const season = seasonOf(now.getMonth(), isSouthern(timeZone))
  const holiday = decorations ? holidayOn(now) : null
  const { outfit, headwear } = dressFor(season, holiday)

  const image = choice === 'org-image' ? images.org : choice === 'my-image' ? images.mine : null
  const time: TimeOfDay =
    choice === 'auto' || choice === 'org-image' || choice === 'my-image' ? timeOfDay(now.getHours()) : choice

  return { time, season, holiday, outfit, headwear, image }
}

// ------------------------------------------------------------------ viewer's choice

const CHOICE_KEY = 'devpulse:scenery'
const MY_IMAGE_KEY = 'devpulse:scenery-image'
/** For previews and checks: a date to dress the room for instead of today, as YYYY-MM-DD. */
const PREVIEW_DATE_KEY = 'devpulse:scenery-date'

export function storedChoice(): SceneryChoice {
  try {
    const value = localStorage.getItem(CHOICE_KEY)
    return value && (['auto', 'org-image', 'my-image', ...TIMES] as string[]).includes(value) ? (value as SceneryChoice) : 'auto'
  } catch {
    return 'auto'
  }
}

export function storeChoice(choice: SceneryChoice): void {
  try {
    localStorage.setItem(CHOICE_KEY, choice)
  } catch {
    // Storage blocked: the choice holds for this visit.
  }
}

export function storedImage(): string | null {
  try {
    return localStorage.getItem(MY_IMAGE_KEY)
  } catch {
    return null
  }
}

/** Keeps a viewer's own image in their browser. False when the browser would not store it. */
export function storeImage(dataUrl: string | null): boolean {
  try {
    if (dataUrl) localStorage.setItem(MY_IMAGE_KEY, dataUrl)
    else localStorage.removeItem(MY_IMAGE_KEY)
    return true
  } catch {
    return false
  }
}

export function previewDate(): Date | null {
  try {
    const value = localStorage.getItem(PREVIEW_DATE_KEY)
    if (!value || !/^\d{4}-\d{2}-\d{2}(T\d{2})?/.test(value)) return null
    const [datePart, hourPart] = value.split('T')
    const [y, m, d] = datePart!.split('-').map(Number)
    return new Date(y!, m! - 1, d!, hourPart ? Number(hourPart.slice(0, 2)) : new Date().getHours())
  } catch {
    return null
  }
}

// ------------------------------------------------------------------ images

/** What an uploaded background should be. Shown beside the upload, and checked before it. */
export const IMAGE_RULES = {
  types: ['image/jpeg', 'image/png', 'image/webp'],
  maxBytes: 2 * 1024 * 1024,
  idealWidth: 3000,
  idealHeight: 1000,
  minWidth: 1500,
  minHeight: 500,
} as const

/**
 * Shrinks an image to at most the ideal size and re-encodes it as JPEG, in the browser.
 *
 * An uploaded photo is often several megabytes of detail nobody sees behind a dimmed wall; a
 * 3000-pixel JPEG is plenty and fits a browser's storage. Re-encoding also means what is stored is
 * pixels the browser decoded, not whatever else the file carried.
 */
export async function prepareImage(file: File): Promise<{ dataUrl: string; width: number; height: number; blob: Blob }> {
  const url = URL.createObjectURL(file)
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = () => reject(new Error('unreadable'))
      img.src = url
    })

    const scale = Math.min(1, IMAGE_RULES.idealWidth / image.naturalWidth, IMAGE_RULES.idealHeight * 1.5 / image.naturalHeight)
    const width = Math.round(image.naturalWidth * scale)
    const height = Math.round(image.naturalHeight * scale)

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('unreadable')
    context.drawImage(image, 0, 0, width, height)

    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('unreadable'))), 'image/jpeg', 0.84),
    )
    const dataUrl = canvas.toDataURL('image/jpeg', 0.84)
    return { dataUrl, width: image.naturalWidth, height: image.naturalHeight, blob }
  } finally {
    URL.revokeObjectURL(url)
  }
}

// ------------------------------------------------------------------ context

export const SceneryContext = createContext<Scenery>(DEFAULT_SCENERY)

export function useScenery(): Scenery {
  return useContext(SceneryContext)
}
